import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Box, Cylinder, Html } from '@react-three/drei';
import * as THREE from 'three';
import { tokens } from '@fluentui/react-components';
import { OverlayBtn, overlayWrapStyle } from './SingleCabinet3D';
import { isNearPanelEdge, isNearPanelEdgeWorld, HoverEdgeFrame, PanelDepthTooltip } from '../utils/edgeHover.jsx';

// --- PAKEITIMAS 9: UnifiedBase tipai suderinti su Basis.jsx id'ais ---
// Basis.jsx ids: 0=Nepasirinkta, 1=Padas, 2=Cokolis, 3=KairėIkiGrindų,
//                4=DešinėIkiGrindų, 5=BePageindo, 6=Kojelės, 7=PlokštėTarp, 8=PlokštėPerVisą

const PanelEdges = ({ args, position }) => {
  const obj = useMemo(() => {
    const [w, h, d] = args;
    const z = d / 2 + 0.001;
    const pts = new Float32Array([
      -w / 2, -h / 2, z,
       w / 2, -h / 2, z,
       w / 2,  h / 2, z,
      -w / 2,  h / 2, z,
    ]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    const mat = new THREE.LineBasicMaterial({ color: '#1a1a1a' });
    return new THREE.LineLoop(geo, mat);
  }, [args[0], args[1], args[2]]);
  useEffect(() => () => { obj.geometry.dispose(); obj.material.dispose(); }, [obj]);
  return <primitive object={obj} position={position} />;
};

// Pagrindo (basisType 1/2/3/4) priekinė cokolio/apdailos juosta dabar gauna TĄ PATĮ
// "Ištraukti į priekį (fasadų plokštuma)" / "Grąžinti į karkaso plokštumą" perjungiklį,
// kokį jau turi SingleCabinet3D.jsx karkaso/pertvarų plokštės (žr. ten CabinetPanel —
// isSelected/hover/toggle mygtukas su tuo pačiu OverlayBtn). Vartotojas: "dabar reikia
// pagrindo plokštėms pritaikyti 'ištraukti į priekį' logiką ir funkcionalumą". Taikoma
// TIK vertikaliai stovinčiai priekinei juostai (1/2/3/4) — basisType 7/8 horizontali
// grindų plokštė neturi analogiškos "fasado plokštumos" sąvokos (ji GULI, o ne stovi
// vertikaliai priekyje), tad jai šis perjungiklis neturi fizinės prasmės ir nepridėtas.
const FrontTrimBox = ({ args, position, color, ghostOpacity = 1, showEdges = false, activeStep, isFacade = false, isSelected = false, onSelectSelf = null, onToggleFront = null, cabinetDepth = 0, hitExtraX = 0, edgeOnly = true }) => {
  const [edgeHovered, setEdgeHovered] = useState(false);
  const [depthHovered, setDepthHovered] = useState(false);
  // Žr. CabinetPanel identišką edgeHoveredRef komentarą — click tikrina PASKUTINĮ
  // onPointerMove rezultatą, ne perskaičiuoja iš naujo su savo paties uv, kad hover
  // (žalia) ir click (mygtukas) VISADA sutaptų.
  const edgeHoveredRef = useRef(false);
  const isIsore = activeStep === 'Išorė';
  const isHoverable = isIsore && !!onSelectSelf;
  // Senas mėlynas "užpildo VISĄ paviršių" švytėjimas pakeistas žaliu RĖMELIU aplink
  // plokštės briauną (žr. HoverEdgeFrame žemiau) — vartotojas: "nereikia tada zymeti
  // melynai visos plokstes... uzejus pelyte ant KRASTINES ji pasizymetu zalia spalva".
  const ei = 0;
  // Edge-only apribojimas DABAR taikomas IR matomam paviršiui, IR ištemptam nematomam
  // "hit" Box'ui (žr. showHit žemiau) — vartotojas: "hover pasidarytu ir paspausti butu
  // galima tik ant plokstes krastines/briaunos o ne ant visos plokstes". Anksčiau hit
  // Box'as (jo vienintelė paskirtis — laimėti raycast konkurenciją prieš kaimyninę
  // plokštę itin status kameros kampu, žr. hitWidthX/hitExtraX komentarą žemiau)
  // SĄMONINGAI neturėjo šio apribojimo, nes jo args[0]/args[1] skiriasi nuo matomos
  // plokštės — e.uv (0..1 PAGAL HIT Box'o matmenis) su isNearPanelEdge (tikisi UV PAGAL
  // MATOMOS plokštės matmenis) duotų neteisingą rezultatą. Sprendimas: isNearPanelEdgeWorld
  // naudoja e.point (pasaulio koordinatės, nepriklauso nuo to, kurį konkretų Box'ą
  // spindulys pagavo) — hit Box'as TOLIAU laimi raycast konkurenciją (plati zona), bet
  // real'us hover/click lieka apribotas iki TIKROS (matomos) plokštės briaunos.
  const hoverHandlers = {
    // setDepthHovered — žr. PanelDepthTooltip importo komentarą: informacinis "Gylis"
    // tooltip'as veikia ant VISO paviršiaus, nepriklausomai nuo isHoverable.
    onPointerMove: (e) => {
      setDepthHovered(true);
      if (!isHoverable) return;
      e.stopPropagation();
      const near = edgeOnly ? isNearPanelEdge(e, args[0], args[1], undefined, edgeHoveredRef.current) : true;
      edgeHoveredRef.current = near;
      setEdgeHovered(near);
      document.body.style.cursor = near ? 'pointer' : '';
    },
    onPointerOut: () => { setDepthHovered(false); edgeHoveredRef.current = false; setEdgeHovered(false); if (isHoverable) document.body.style.cursor = ''; },
    // stopPropagation PRIEŠ briaunos patikrą — žr. SingleCabinet3D.jsx CabinetPanel
    // komentarą apie tą pačią "paspaudimas iškrenta iki FurnitureGroup" klaidą.
    onClick: isIsore && onSelectSelf ? (e) => { e.stopPropagation(); if (!edgeHoveredRef.current) return; onSelectSelf(); } : undefined,
  };
  const hitOnlyHandlers = {
    onPointerMove: (e) => {
      setDepthHovered(true);
      if (!isHoverable) return;
      e.stopPropagation();
      const near = edgeOnly ? isNearPanelEdgeWorld(e, args[0], args[1], undefined, edgeHoveredRef.current) : true;
      edgeHoveredRef.current = near;
      setEdgeHovered(near);
      document.body.style.cursor = near ? 'pointer' : '';
    },
    onPointerOut: () => { setDepthHovered(false); edgeHoveredRef.current = false; setEdgeHovered(false); if (isHoverable) document.body.style.cursor = ''; },
    // stopPropagation PRIEŠ briaunos patikrą — žr. SingleCabinet3D.jsx CabinetPanel
    // komentarą apie tą pačią "paspaudimas iškrenta iki FurnitureGroup" klaidą.
    onClick: isIsore && onSelectSelf ? (e) => { e.stopPropagation(); if (!edgeHoveredRef.current) return; onSelectSelf(); } : undefined,
  };
  // DividerDragHandle (žr. ten "Full-depth hit box for Išorė") savo pertvaros paspaudimo
  // zoną Išorės žingsnyje TYČIA ištempia per visą gylį (z=[0,depth+0.05]), kad pertvara
  // liktų paspaudžiama iš bet kokio kameros kampo. Kadangi kamera žiūri iš aukščiau/kampo
  // (ne stačiu kampu), tas ištemptas stačiakampis gretimoje X vietoje gali "užstoti"
  // spindulį TIKSLIAI ten, kur stovi vidinė pertvara — net jei jos pačios Y riba
  // (aukščiau bazės zonos) formaliai nesikerta su šia priekine juosta. Be atskiro,
  // TOKIO PAT ištempto paspaudimo ploto ir ŠIAI juostai, ta viena X kolonėlė (lygiai
  // ten, kur stovi vidinė pertvara) likdavo nepaspaudžiama — vartotojas: "nepavyksta man
  // paspausti". Pridėtas nematomas (opacity:0, bet vis tiek raycast'inamas) "hit" Box,
  // kurio priekinis paviršius siekia TĄ PATĮ (ar truputį didesnį) gylį, kaip pertvaros
  // — dabar abi zonos "konkuruoja" vienodomis sąlygomis, o ne pertvara visada laimi.
  const hitDepthArg = Math.max(args[2], cabinetDepth + 0.06);
  // Nematoma dėžė turi apimti pasaulio Z=[0, hitDepthArg] (nuo tikro spintos galo iki
  // priekio su atsarga) — grupė (position) jau perkelta į priekinės juostos Z, tad čia
  // atimame TIK tą poslinkį (position[2]), NE dar ir pačios plonos juostos pusę storio.
  const hitPosZ = hitDepthArg / 2 - position[2];
  // Ištemptas hitbox'as siauresnis (per 0.1 iš viso) nei pati matoma priekinė juosta —
  // palieka saugią paraštę, kad nesiektų ten, kur prasideda kaimyninė šoninė dėžė.
  const hitWidthArg = Math.max(0.01, args[0] - 0.1);
  // hitExtraX: siaura (wallThickness pločio) šoninė "cokolio" dėžė (elemProps('left'/
  // 'right')), stovinti TIESIOG greta priekinės juostos, PRARASDAVO paspaudimo
  // konkurenciją prieš ją — patikrinta gyvai per rankinį spindulio (ray-box)
  // skaičiavimą su TIKROMIS pasaulio koordinatėmis (page.evaluate + camera.project/
  // unproject). Priežastis NĖRA X plotis, o Z ašis: priekinės juostos ištemptas
  // hitbox (žr. hitDepthArg aukščiau) siekia cabinetDepth+0.06, t.y. 6cm TOLIAU PIRMYN
  // (arčiau kameros) už tikrą baldo priekį — tas "prieš baldą kyšantis" sluoksnis
  // nusidriekia beveik per visą plotį ir pridengia bet ką už jo, nepriklausomai nuo X,
  // kai kamera žiūri iš toli ir smarkiai iš šono. Sprendimas: šiai dėžei duodamas TOKS
  // PAT (truputį didesnis, kad laimėtų lygiais atvejais) Z pratęsimas — žr. UnifiedBase
  // kur šiai dėžei perduodamas cabinetDepth={safeDepth+0.01} — o X ašimi šiek tiek
  // platesnė (hitExtraX), kad tiksliai pataikius į siaurą kraštą apskritai būtų į ką
  // pataikyti. Likusi, retesnė riba: dėl itin status kameros kampo į VIENĄ konkretų
  // šoną (žr. FurnitureGroup camera setup), viena iš dviejų šoninių dėžių vis dar gali
  // pasitaikyti sunkiau paspaudžiama tiksliai ties riba su priekine juosta — ta pati,
  // jau anksčiau vartotojui atskleista klasė problemų, kaip pertvaros/priekinės juostos
  // ribinis atvejis (žr. DividerDragHandle komentarą aukščiau).
  const hitWidthX = Math.max(hitExtraX > 0 ? args[0] + hitExtraX * 2 : 0, cabinetDepth > 0 ? hitWidthArg : 0);
  // && !!onSelectSelf — KRITIŠKAI SVARBU, kai šis elementas tyčia padarytas
  // neinteraktyviu (onSelectSelf=null — žr. Cokolio 'front' komentarą aukščiau, kuris
  // dabar tik SEKA Apačios būseną). Be šito apribojimo, šis (nematomas, bet VIS TIEK
  // raycast'inamas) EXTRA_UP/hitDepthArg ištemptas hit-box'as toliau renderintųsi — ir
  // nors jo PAČIO onClick tampa `undefined` (žr. hitOnlyHandlers žemiau), r3f event
  // sistema (patikrinta tiesiogiai @react-three/fiber šaltinyje) NEPRALEIDŽIA įvykio
  // tiesiog TOLIAU prie kito, tolimesnio objekto (Apačios) — ji BUBLINA AUKŠTYN šio
  // objekto PROTĖVIŲ grandine, ieškodama BET KURIO protėvio su onClick, ir suranda
  // FurnitureGroup.jsx viršutinio lygio <group onClick> (kuris visada iškviečia
  // e.stopPropagation()) — PRIEŠ Apačios pačios (tolimesnio, bet TEISINGO) raycast
  // atitikmens eilę apskritai pasiekiant. Kadangi ši dėžė VIS DAR turėdavo savo
  // onPointerMove/onPointerOut (vien dėl "Gylis" tooltip'o — žr. depthHovered
  // aukščiau, tyčia visada veikiantis), ji VIS TIEK registruodavosi r3f interakcijos
  // sąraše net ir onClick=undefined atveju — vartotojas: "jeigu pelyte lengvai uzejus
  // briauna pazaliuoja [=Apačios PAČIOS hover, kuris pasiekia Apačią, nes onPointerMove
  // bublinimas NERANDA jokio onClick analogo] ir tai sistema identifikuoja, kodel tada
  // paspaudimas neveikia?" — nes click bublinimas (SKIRTINGAI nuo move) IŠ TIKRŲJŲ
  // randa FurnitureGroup lygmenį ir ten sustoja, dar PRIEŠ pasiekiant Apačios pačios
  // (toliau raycast eilėje esantį, bet visiškai teisingą) onClick. Pašalinus šią dėžę
  // VISAI (ne tik jos onClick), ji daugiau NEBEDALYVAUJA raycast'e iš viso — Apačios
  // click nebeturi su kuo "lenktyniauti".
  const showHit = (cabinetDepth > 0 || hitExtraX > 0) && !!onSelectSelf;
  // Y ašimi (aukštis) ištempiama DAUGIAU Į VIRŠŲ nei žemyn — vartotojas: "nepavyksta
  // paspausti ant pagrindo plokštes" (patikrinta gyvai per scenos introspekciją/tikslų
  // spindulio taikymą: viršutinė pusė šios juostos PRARASDAVO raycast konkurenciją prieš
  // virš jos esantį KORPUSO "Bottom wall" skydą / dureles — tas pats "status kameros
  // kampas, ištemptas kaimyno hitbox'as" reiškinys, aprašytas hitWidthX komentare
  // aukščiau, tik Y, ne X ašimi). EXTRA_UP žymiai didesnis už EXTRA_DOWN, nes apačioje
  // (grindys) konkuruojančios geometrijos nėra.
  // 0.04 NEPAKAKO: po edge-only apribojimo (žr. isNearPanelEdgeWorld aukščiau) vartotojas
  // gyvai aptiko siaurą, bet TIKRĄ juostelę TIESIOG VIRŠ anksčiau ištemptos zonos, kur
  // paspaudimas (ne hover — tas jau rodė žalią) pakliūdavo durų sekcijai, ne šiai juostai
  // (patikrinta sistemingu pikselis-po-pikselio nuskaitymu, žr. [[feedback_panel_click_hitbox_competition]]
  // — tiksli siaura "spraga" tarp ištempto hitbox'o viršaus ir durų apačios). Padidinta iki
  // 0.12, kad hitbox'as siektų GEROKAI UŽ tos spragos, ne vien iki jos.
  const EXTRA_UP = 0.12;
  const EXTRA_DOWN = 0.01;
  const hitHeightArg = args[1] + EXTRA_UP + EXTRA_DOWN;
  const hitPosY = (EXTRA_UP - EXTRA_DOWN) / 2;
  return (
    <group position={position}>
      <Box args={args} {...hoverHandlers}>
        {/* Bendra ryškumo "grindis" (0.3) PAČIA plokštės spalva — žr. identišką
            SingleCabinet3D.jsx CabinetPanel komentarą: įgaubto 90° kampo patamsėjimas. */}
        <meshStandardMaterial color={color} transparent={ghostOpacity < 1} opacity={ghostOpacity} emissive={color} emissiveIntensity={0.3 + ei} />
      </Box>
      {showHit && (
        <Box args={[hitWidthX, hitHeightArg, hitDepthArg]} position={[0, hitPosY, hitPosZ]} {...hitOnlyHandlers}>
          <meshStandardMaterial transparent opacity={0} depthWrite={false} />
        </Box>
      )}
      {showEdges && <PanelEdges args={args} position={[0, 0, 0]} />}
      <HoverEdgeFrame width={args[0]} height={args[1]} depth={args[2]} visible={isHoverable && (edgeHovered || isSelected)} />
      <PanelDepthTooltip depth={args[2]} position={[0, 0, args[2] / 2 + 0.02]} visible={depthHovered} />
      {isIsore && isSelected && onToggleFront && (
        <Html position={[0, 0, args[2] / 2 + 0.02]} center zIndexRange={[175, 0]} style={{ pointerEvents: 'none' }}>
          <div style={overlayWrapStyle}>
            <OverlayBtn
              title={isFacade ? "Grąžinti į karkaso plokštumą" : "Ištraukti į priekį (fasadų plokštuma)"}
              onClick={(e) => { e.stopPropagation(); onToggleFront(); }}
            >
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300, color: isFacade ? '#09aa89' : undefined }}>
                swap_horiz
              </span>
            </OverlayBtn>
          </div>
        </Html>
      )}
    </group>
  );
};

// Apvalios kojelės (basisType 6) su ta pačia hover/select/toggle logika kaip FrontTrimBox —
// atskiras komponentas (ne <Box>), nes kojelė yra Cylinder, o useState turi priklausyti
// ŠIAM konkrečiam elementui (kiekviena kojelė šviečia individualiai užvedus pelę), nors
// pažymėjimo būsena (isSelected) dalinama VISOMS kojelėms iš karto (jos sudaro vieną
// pažymimą "Kojelės" vienetą, ne po vieną atskirai — žr. UnifiedBase elemProps('legs')).
const HoverableLeg = ({ position, radius, height, color, ghostOpacity, activeStep, isSelected = false, onSelectSelf = null }) => {
  const [hovered, setHovered] = useState(false);
  const isIsore = activeStep === 'Išorė';
  const isHoverable = isIsore && !!onSelectSelf;
  const ei = isSelected ? 0.4 : (isHoverable && hovered ? 0.25 : 0);
  return (
    <Cylinder
      position={position} args={[radius, radius, height, 16]}
      onPointerOver={(e) => { e.stopPropagation(); if (isHoverable) { setHovered(true); document.body.style.cursor = 'pointer'; } }}
      onPointerOut={() => { setHovered(false); if (isHoverable) document.body.style.cursor = ''; }}
      onClick={isIsore && onSelectSelf ? (e) => { e.stopPropagation(); onSelectSelf(); } : undefined}
    >
      {/* Kojelė (Cylinder) neturi plokščio paviršiaus su aiškia briauna, tad edge-only
          apribojimas jai fiziškai neprasmingas — bet spalva vis tiek suvienodinta su
          likusiomis plokštėmis (žalia, ne mėlyna), žr. edgeHover.jsx komentarą. */}
      <meshStandardMaterial color={color} transparent={ghostOpacity < 1} opacity={ghostOpacity} metalness={0.8} roughness={0.2} emissive="#22c55e" emissiveIntensity={ei} />
    </Cylinder>
  );
};

const UnifiedBase = ({
  type: rawType, totalWidth, height, depth, wallThickness, color = "#666666", ghostOpacity = 1, showEdges = false,
  activeStep,
  // Bendras, elemento-id pagrįstas pažymėjimo/fasado mechanizmas VISIEMS šio komponento
  // matomiems elementams (priekinė juosta, šoninės cokolio dėžės, kojelės, grindų plokštė)
  // — anksčiau tik VIENA (priekinė juosta) turėjo isSelected/onSelectSelf/onToggleFront/
  // isFacade props, o likusios (šoninės dėžės 2/3/4, kojelės 6, plokštė 7/8) VISIŠKAI
  // neturėjo jokio hover/select elgesio — vartotojas: "visu ploksciu pazymejimas turi
  // buti vienodas visur". furnitureId + tas pats raktų schema, kaip SingleCabinet3D.jsx
  // carcassPanelToggleProps (outerpanel:${furnitureId}-base:${id} / ${furnitureId}-base-${id}),
  // tik su "base" kaip pastoviu "cabinetIndex", kad niekada nesusikirstų su tikrais
  // kabinetų indeksais (žr. FurnitureGroup.jsx komentarą prie senojo vienintelio 'front' rakto).
  furnitureId = null, selectedDividerKey = null, onDividerSelect = null,
  panelFrontOffsets = null, onTogglePanelFront = null,
  // ClickUp "2.0 Baldas. Karkaso gylis. Pagrindai/Šonai 60cm be durų" — žr.
  // FRONT_RECESS žemiau; default true = sena (visada įgilinta) elgsena, jei
  // kviečiantysis šio prop'o neperduoda.
  anyDoorInGroup = true,
}) => {
  const MIN_DIMENSION = 0.001;

  // ANKSČIAU čia buvo BASIS_TYPE_SWAP_1234 (žr. SingleCabinet3D.jsx identišką komentarą
  // dėl kodėl jis pašalintas — type===1/2/3/4 blokai žemiau jau savarankiškai patikrinti
  // prieš savo pačių numerio referencinę ikoną, tad SWAP dabar tik vėl viską rodydavo
  // atvirkščiai). Šis failas TURI naudoti TĄ PATĮ (nepaskeistą) basisType, kaip
  // SingleCabinet3D.jsx, kitaip šio komponento dėžė (šonai/priekis) nesutaptų su
  // tikrų šoninių sienelių floor-reach elgesiu.
  const type = rawType;

  const safeTotalWidth = Math.max(MIN_DIMENSION, Number(totalWidth) || 0);
  const safeHeight = Math.max(MIN_DIMENSION, Number(height) || 0);
  const safeDepth = Math.max(MIN_DIMENSION, Number(depth) || 0);
  const safeWallThickness = Math.max(MIN_DIMENSION, Number(wallThickness) || 0.02);

  const legRadius = 0.025;
  const legInset = 0.06;

  // ClickUp "2.0 Baldas. Pagrindas Cokolis/Kairė/Dešinė iki grindų. Padaryti cokolio
  // plokštę per visą plotį." — anksčiau čia buvo PLINTH_INSET (3cm), kuris įgilindavo
  // dėžę (priekį IR šonus) nuo baldo krašto PER PLOTĮ — tai buvo sąmoningas ankstesnis
  // sprendimas, atitinkantis pagrindas/{2,3,4}.jpg ikonas, bet vartotojas GYVAI
  // patikrino ir paaiškino, kad tai NETEISINGA kryptis: "Jau beveik gerai. Kažkodėl
  // paskutinėje versijoje sumažėjo cokolio plotis. Reiktų daryti cokolį per visą
  // baldo plotį... tas cokolinė lenta eina per visą baldo plotį, o šonuose dėsis
  // falšjuostos. Jei paliksime kaip yra, liks keistai atrodantys tarpeliai." T.y.
  // pločio siaurinimas NĖRA pageidaujamas — jei vartotojas nori siauresnio korpuso,
  // jis naudoja ATSKIRĄ "Falšjuosta"/"Papildomas šonas" funkciją (žr. calculatePrice.js
  // sideElementWidthM), ne šio pagrindo dėžės savaiminį siaurėjimą. PLINTH_INSET
  // PAŠALINTAS — dėžė dabar PILNO baldo pločio, įgilinta TIK gyliu (FRONT_RECESS
  // žemiau, lieka nepakitęs — tai atskira, jau patvirtinta savybė).
  // ClickUp "2.0 Baldas. Pagrindas. Padas/Cokolis/Kairė/Dešinė. Priekinės lentos
  // įgilinimas." — priekinė juosta anksčiau būdavo įgilinta vos ~1-1,2cm (atskirai
  // priklausydavo nuo PLINTH_INSET/wallThickness aritmetikos kiekviename tipe), o
  // vartotojas GYVAI patikrino ir pasakė, kad ji vizualiai atrodo "išlindusi į
  // priekį" — reikia VISADA lygiai 2cm nuo baldo priekio (durų, jei yra; karkaso,
  // jei ne — durų "reveal" tarpas nuo karkaso yra vos ~3mm, per mažas, kad vertėtų
  // atskirai modeliuoti abu atvejus, tad skaičiuojama nuo karkaso priekio visais
  // atvejais). Šoninė dėžė (tipams 2/3/4) lieka įgilinta GILIAU už priekį lygiai
  // wallThickness storiu, kad priekis TOLIAU "uždengtų" šonus (žr. ankstesnį
  // "priekinė dengia šonines" fix'ą) — ne todėl, kad šonai turėtų savo atskirą,
  // nepriklausomą gylio taisyklę.
  // Vartotojas gyvai patikrino 2cm variantą ir paprašė dar giliau — "atrodo jog
  // igylinta vos kelis milimetrus" — tad padidinta iki 4cm.
  // PATAISYTA: 4cm buvo UŽKIETINTA konstanta, teisinga tik atsitiktinai, kai
  // safeWallThickness == 2cm. Tikroji taisyklė (vartotojas): "pagrindo cokolis
  // yra visada 2cm labiau įgilintas nuo pagrindo plokštės, kuri gali būti arba
  // karkaso plokštumoje, arba fasadų plokštumoje, kitų gylių negali būti". Apačia
  // (SingleCabinet3D.jsx "Bottom wall") stovi karkaso plokštumoje — t.y. fasadas
  // MINUS 1×safeWallThickness (tą automatiškai atlieka CabinetPanel'io bordersDoor
  // mechanizmas, žr. ten komentarą). Cokolio juosta turi būti dar 1×safeWallThickness
  // giliau už TĄ, tad nuo fasado: 2×safeWallThickness — ne nepriklausoma 4cm konstanta.
  // ClickUp "2.0 Baldas. Karkaso gylis. Pagrindai/Šonai turi būti 60cm (be durų)" —
  // bordersDoor mechanizmas DABAR sąlygiškas (0 arba 1×safeWallThickness, žr.
  // carcassPanelToggleProps SingleCabinet3D.jsx), tad šis "Apačios" terminas čia
  // TURI sekti TĄ PATĮ sąlygą (anyDoorInGroup), kitaip Cokolis "atsiliktų" fiksuotoje
  // vietoje, kol Apačia pasislenka — vėl tas pats Z-laiptelio bug'as, kurį šis
  // skaičiavimas buvo sukurtas išvengti.
  const FRONT_RECESS = (anyDoorInGroup ? safeWallThickness : 0) + safeWallThickness;
  const frontCenterZ = safeDepth / 2 - FRONT_RECESS - safeWallThickness / 2;
  const boxDepthInset = FRONT_RECESS + safeWallThickness;

  // elemProps(id): tas pats key/isFacade/isSelected/onSelectSelf/onToggleFront rinkinys
  // KIEKVIENAM šio komponento pažymimam elementui — 'front' (priekinė juosta),
  // 'left'/'right' (cokolio šoninės dėžės, tipai 2/3/4), 'plate' (grindų plokštė,
  // tipai 7/8), 'legs' (kojelės, tipas 6). Veikia net kai furnitureId/onDividerSelect/
  // onTogglePanelFront nėra paduoti (senas ConfBuilder.jsx kvietimas be jokių šių props) —
  // tada tiesiog viskas lieka neinteraktyvu, kaip ir anksčiau.
  const elemKey = (id) => `outerpanel:${furnitureId}-base:${id}`;
  const elemProps = (id) => {
    const dk = elemKey(id);
    return {
      isFacade: panelFrontOffsets?.[`${furnitureId}-base-${id}`] === 'facade',
      isSelected: selectedDividerKey === dk,
      onSelectSelf: onDividerSelect ? () => onDividerSelect(selectedDividerKey === dk ? null : dk) : null,
      onToggleFront: onTogglePanelFront ? () => onTogglePanelFront(furnitureId, 'base', id) : null,
    };
  };
  // PATAISYTA: priekinė cokolio juosta DAUGIAU NEBETURI savo atskiro pažymėjimo/
  // perjungimo rakto ('base-front'). Vartotojas, paragintas pirmiau padaryto
  // edgeOnly=false bandymo (kuris PATS cokolį padarė paspaudžiamą): "turi buti pazymima
  // ta plokste kur matosi briauna [=Apačia, SingleCabinet3D.jsx CabinetPanel 'bottom']
  // ir tik briaunos plokste istraukineti (kartu su apatine plokste [=Cokolis]
  // lygiagreciai) o ne tik ta apatine plokste" — t.y. Apačia turi būti VIENINTELIS
  // paspaudžiamas/žaliai pažymimas valdymo taškas, o Cokolis tik VIZUALIAI SEKA tą
  // pačią būseną (analogiškai UnifiedTopPanel.jsx Stogelio tipų 3/4 antrinėms dėžėms,
  // kurios irgi tik seka pirmosios isFacade, bet savo mygtuko nerodo). Naudojame TIKSLIAI
  // tą patį raktą, kokį PIRMAM (index=0) kabinetui susikuria SingleCabinet3D.jsx
  // carcassPanelToggleProps('bottom') — jei grupėje yra keli kabinetai, Cokolis (vienas
  // bendras visai grupei, kaip Stogelis) seka KONKREČIAI pirmojo kabineto Apačios būseną.
  const bottomDividerKey = `outerpanel:${furnitureId}-0:bottom`;
  const frontElem = {
    isFacade: panelFrontOffsets?.[`${furnitureId}-0-panel-bottom`] === 'facade',
    isSelected: selectedDividerKey === bottomDividerKey,
    onSelectSelf: null,
    onToggleFront: null,
  };
  // "Ištraukti į priekį" — priekinė juosta pasislenka lygiai vienu safeWallThickness
  // į priekį (ta pati poslinkio norma, kokią jau naudoja SingleCabinet3D.jsx
  // CabinetPanel/DividerDragHandle savo fasado perjungikliui), atšaukdama numatytąjį
  // frontInset įgilinimą.
  const facadeShift = frontElem.isFacade ? safeWallThickness : 0;
  const frontTrimProps = { color, ghostOpacity, showEdges, activeStep, cabinetDepth: safeDepth, ...frontElem };
  // PASTABA: sideElemProps TYČIA NEPERDUODA cabinetDepth (skirtingai nuo frontTrimProps) —
  // FrontTrimBox'o ištemptas nematomas "hit" Box (žr. ten komentarą) reikalingas TIK
  // priekinei juostai, kad konkuruotų su pertvaros (DividerDragHandle) ištemptu hitbox'u
  // TOJE PAČIOJE X srityje. Šoninės dėžės/plokštė/kojelės stovi PAČIAME KRAŠTE, kur
  // pertvaros niekada nebūna, o jų PAČIŲ matoma geometrija JAU apima visą gylį (args[2]=
  // safeDepth) — papildomas, dar didesnis ištemptas hitbox joms ne tik nereikalingas, bet
  // (patikrinta gyvai) net KENKSMINGAS: jis pradėjo "pavogti" paspaudimus nuo priekinės
  // juostos KAIMYNYSTĖJE esančių elementų, nes kelios persidengiančios ištemptos dėžės
  // pradėjo konkuruoti tarpusavyje nenuspėjamai (kampinė kamera, žr. žemiau).
  const sideElemProps = { color, ghostOpacity, showEdges, activeStep };

  // Tipai 0 (Nepasirinkta) ir 5 (Be pagrindo) neturi pagrindo
  if (type === 0 || type === 5 || safeHeight <= MIN_DIMENSION) return null;

  // 1: Padas — PERDARYTA. Anksčiau čia buvo pilna 4-sienelių dėžė (pjedestalas),
  // ant kurio baldas tarsi "stovėdavo", o TIKRI baldo šoniniai skydai sustodavo
  // ties šios dėžės viršumi (nesiekdavo grindų). Dabar TIKRI šoniniai skydai patys
  // siekia grindis (žr. SingleCabinet3D.jsx sideBottomOffset — basisType 1 elgiasi
  // taip pat, kaip 5 "Be pagrindo", tik su papildoma dugno plokšte tarp šonų,
  // kurią jau piešia esama "Bottom wall" karkaso plokštė, nekeičiant nieko čia).
  // Šiai dėžei tada beliktų tik PRIEKINĖ cokolio apdailos juosta (identiška type 2
  // "Cokolis" priekinei lentai) — šonų/galo dalys pašalintos, kad nesidubliuotų su
  // dabar matomais tikrais šoniniais skydais (žr. ClickUp "2.0 Baldas. Pagrindas.
  // Padas. Abu šonai iki grindų. Cokolis ir dugnas tarp šonų").
  // Priekinės juostos plotis ĮGILINTAS safeWallThickness IŠ ABIEJŲ PUSIŲ VISIEMS
  // tipams 1-4 — "Baldo plokštes niekada negali persikirsti viena su kita" auditas
  // parodė, kad abu kraštai VISADA turi ten kažką (arba SAVĄ cokolio dėžę — tipai
  // 2/3/4, arba TIKRĄ grindis siekiantį SingleCabinet3D.jsx šoną — tipas 1 abiem
  // pusėm, tipas 3 kairėje, tipas 4 dešinėje), su kuriuo pilno pločio juosta
  // persidengdavo (tas pats "susikirtimo" bug'as, koks jau buvo rastas ir taisytas
  // type 8 plokštei — čia liko nepataisytas priekinės juostos komponente). Anksčiau
  // tipai 3/4 įgilindavo TIK toje pusėje, kur PALIEKA savo dėžę — dabar abi pusės
  // vienodai, nes ir kita (grindis siekianti) pusė TAIP PAT turi ten kažką.
  const frontArgs = [Math.max(MIN_DIMENSION, safeTotalWidth - safeWallThickness * 2), safeHeight, safeWallThickness];
  const frontPos = [0, safeHeight / 2, frontCenterZ + facadeShift];

  if (type === 1) {
    return (
      <group>
        <FrontTrimBox args={frontArgs} position={frontPos} {...frontTrimProps} />
      </group>
    );
  }

  // 2: Cokolis — full-width plinth BOX (žr. "per visą plotį" fix'o komentarą
  // aukščiau) — only the DEPTH is inset (FRONT_RECESS), matching the real
  // SingleCabinet3D.jsx side walls (which stop at visualBaseHeight for this type,
  // they do NOT reach the floor) sitting on TOP of this box.
  if (type === 2) {
    const leftElem = elemProps('left');
    const rightElem = elemProps('right');
    const boxHalfWidth = safeTotalWidth / 2;
    const boxDepth = Math.max(MIN_DIMENSION, safeDepth - boxDepthInset);
    const boxFrontZ = safeDepth / 2 - boxDepthInset;
    const boxCenterZ = boxFrontZ - boxDepth / 2;
    // Priekinė lenta DENGIA šonines (jos plotis = VISAS dėžės plotis, o ne tarp šonų
    // įgilinta) — anksčiau priekis buvo siauresnis ir stovėjo TARP dviejų šoninių
    // dėžių (matėsi šoninės dėžės priekinė briauna atskira juostele šone, žr.
    // vartotojo screenshot su raudonais rėmeliais). Dabar priekis pilno dėžės pločio
    // ir stovi VIENU wallThickness ARČIAU kameros už šonus (jos "užsideda ant" jų),
    // pilnai pridengdama jų priekinę briauną — jos OUTER paviršius lygiai
    // frontCenterZ+wallThickness/2, t.y. FRONT_RECESS nuo tikro baldo priekio.
    const cokolisFrontArgs = [boxHalfWidth * 2, safeHeight, safeWallThickness];
    const cokolisFrontPos = [0, safeHeight / 2, frontCenterZ + facadeShift];
    const leftArgs = [safeWallThickness, safeHeight, boxDepth];
    const leftPos = [-boxHalfWidth + safeWallThickness / 2, safeHeight / 2, boxCenterZ + (leftElem.isFacade ? 0.006 : 0)];
    const rightArgs = [safeWallThickness, safeHeight, boxDepth];
    const rightPos = [boxHalfWidth - safeWallThickness / 2, safeHeight / 2, boxCenterZ + (rightElem.isFacade ? 0.006 : 0)];
    return (
      <group>
        <FrontTrimBox args={cokolisFrontArgs} position={cokolisFrontPos} {...frontTrimProps} />
        <FrontTrimBox args={leftArgs} position={leftPos} hitExtraX={0.1} cabinetDepth={safeDepth + 0.01} {...sideElemProps} {...leftElem} />
        <FrontTrimBox args={rightArgs} position={rightPos} hitExtraX={0.1} cabinetDepth={safeDepth + 0.01} {...sideElemProps} {...rightElem} />
      </group>
    );
  }

  // 3: Kairė iki grindų — LEFT side goes to floor (no skirting), RIGHT side keeps
  // the skirting/cokolis box. FIXED: this block and type 4 below had left/right
  // swapped — confirmed against the reference icon (assets/img/icons/pagrindas/3.jpg),
  // which clearly shows the LEFT side flush to the floor and the RIGHT side resting
  // on a recessed plinth board, the opposite of what this block used to draw (see
  // ClickUp "2.0 Baldas. Pagrindas. Kairė/Dešinė iki grindų..."). SingleCabinet3D.jsx
  // leftBottomOffset/rightBottomOffset now make the REAL side panels match this too.
  // Side board runs the full depth (flush at front, like Padas) so the recessed
  // front board is actually visible as a step — see type 2 comment above for why.
  if (type === 3) {
    const rightElem = elemProps('right');
    // Only the RIGHT side carries a plinth box — the LEFT real wall already
    // reaches the floor flush at the true edge, so the front board's left end
    // butts against ITS inner face (true edge + wallThickness), while its right
    // end now reaches the TRUE right edge (full width — žr. "per visą plotį"
    // fix'o komentarą aukščiau), not an inset box edge.
    const boxHalfWidth = safeTotalWidth / 2;
    const boxDepth = Math.max(MIN_DIMENSION, safeDepth - boxDepthInset);
    const boxCenterZ = (safeDepth / 2 - boxDepthInset) - boxDepth / 2;
    const rightArgs = [safeWallThickness, safeHeight, boxDepth];
    const rightPos = [boxHalfWidth - safeWallThickness / 2, safeHeight / 2, boxCenterZ + (rightElem.isFacade ? 0.006 : 0)];
    // Priekis dabar dengia dešinę dėžę (jos plotis siekia dėžės IŠORINĮ, ne vidinį,
    // kraštą, ir stovi wallThickness arčiau kameros) — žr. type 2 komentarą aukščiau.
    // Kairėje pusėje (tikra grindis siekianti sienelė) elgesys nesikeičia — vienintelis
    // įgilinimas ten yra safeWallThickness, kad priekis atsiremtų į tikros sienelės
    // vidinį paviršių, be jokio papildomo pločio siaurinimo.
    const cokolisFrontArgs = [Math.max(MIN_DIMENSION, safeTotalWidth - safeWallThickness), safeHeight, safeWallThickness];
    const cokolisFrontPos = [safeWallThickness / 2, safeHeight / 2, frontCenterZ + facadeShift];
    return (
      <group>
        <FrontTrimBox args={cokolisFrontArgs} position={cokolisFrontPos} {...frontTrimProps} />
        <FrontTrimBox args={rightArgs} position={rightPos} hitExtraX={0.1} cabinetDepth={safeDepth + 0.01} {...sideElemProps} {...rightElem} />
      </group>
    );
  }

  // 4: Dešinė iki grindų — RIGHT side goes to floor (no skirting), LEFT side keeps
  // the skirting/cokolis box. Fixed alongside type 3 above — same swapped-sides
  // bug, confirmed backwards against pagrindas/4.jpg (RIGHT flush to floor, LEFT
  // on a recessed plinth board).
  // Side board runs the full depth (flush at front, like Padas) — see type 2.
  if (type === 4) {
    const leftElem = elemProps('left');
    // Mirror of type 3 above — LEFT carries the plinth box (now full-width, depth
    // still recessed), RIGHT real wall reaches the floor flush at the true edge.
    const boxHalfWidth = safeTotalWidth / 2;
    const boxDepth = Math.max(MIN_DIMENSION, safeDepth - boxDepthInset);
    const boxCenterZ = (safeDepth / 2 - boxDepthInset) - boxDepth / 2;
    const leftArgs = [safeWallThickness, safeHeight, boxDepth];
    const leftPos = [-boxHalfWidth + safeWallThickness / 2, safeHeight / 2, boxCenterZ + (leftElem.isFacade ? 0.006 : 0)];
    // Mirror of type 3's "priekis dengia dėžę" fix above.
    const cokolisFrontArgs = [Math.max(MIN_DIMENSION, safeTotalWidth - safeWallThickness), safeHeight, safeWallThickness];
    const cokolisFrontPos = [-safeWallThickness / 2, safeHeight / 2, frontCenterZ + facadeShift];
    return (
      <group>
        <FrontTrimBox args={cokolisFrontArgs} position={cokolisFrontPos} {...frontTrimProps} />
        <FrontTrimBox args={leftArgs} position={leftPos} hitExtraX={0.1} cabinetDepth={safeDepth + 0.01} {...sideElemProps} {...leftElem} />
      </group>
    );
  }

  // 6: Kojelės --- PAKEITIMAS 9: Kojelės renderuojamos teisingai, matomos priekyje ---
  // Visos kojelės kartu sudaro VIENĄ pažymimą "Kojelės" vienetą (bendra isSelected/
  // onToggleFront reikšmė elemProps('legs')) — kiekviena šviečia individualiai užvedus
  // pelę (žr. HoverableLeg), bet paspaudus BET KURIĄ pažymima visa grupė ir mygtukas
  // rodomas TIK prie priekinės kairės kojelės (kad nesikartotų prie kiekvienos).
  if (type === 6) {
    const legsElem = elemProps('legs');
    const legsFacadeShift = legsElem.isFacade ? 0.006 : 0;
    const adjustedLegInset = Math.max(0.04, legInset);
    const legProps = { color: "#aaaaaa", ghostOpacity, activeStep, isSelected: legsElem.isSelected, onSelectSelf: legsElem.onSelectSelf };
    const legZ = (front) => (front ? safeDepth / 2 - adjustedLegInset : -safeDepth / 2 + adjustedLegInset) + legsFacadeShift;
    return (
      <group>
        {/* Priekinės kairės */}
        <HoverableLeg position={[-safeTotalWidth / 2 + adjustedLegInset, safeHeight / 2, legZ(true)]} radius={legRadius} height={safeHeight} {...legProps} />
        {/* Priekinės dešinės */}
        <HoverableLeg position={[safeTotalWidth / 2 - adjustedLegInset, safeHeight / 2, legZ(true)]} radius={legRadius} height={safeHeight} {...legProps} />
        {/* Galinės kairės */}
        <HoverableLeg position={[-safeTotalWidth / 2 + adjustedLegInset, safeHeight / 2, legZ(false)]} radius={legRadius} height={safeHeight} {...legProps} />
        {/* Galinės dešinės */}
        <HoverableLeg position={[safeTotalWidth / 2 - adjustedLegInset, safeHeight / 2, legZ(false)]} radius={legRadius} height={safeHeight} {...legProps} />
        {/* Vidurio kojelės jei baldas platesnis nei 1.2m */}
        {safeTotalWidth > 1.2 && (
          <>
            <HoverableLeg position={[0, safeHeight / 2, legZ(true)]} radius={legRadius} height={safeHeight} {...legProps} />
            <HoverableLeg position={[0, safeHeight / 2, legZ(false)]} radius={legRadius} height={safeHeight} {...legProps} />
          </>
        )}
        {legsElem.isSelected && activeStep === 'Išorė' && legsElem.onToggleFront && (
          <Html position={[-safeTotalWidth / 2 + adjustedLegInset, safeHeight + 0.04, safeDepth / 2 - adjustedLegInset + legsFacadeShift]} center zIndexRange={[175, 0]} style={{ pointerEvents: 'none' }}>
            <div style={overlayWrapStyle}>
              <OverlayBtn
                title={legsElem.isFacade ? "Grąžinti į karkaso plokštumą" : "Ištraukti į priekį (fasadų plokštuma)"}
                onClick={(e) => { e.stopPropagation(); legsElem.onToggleFront(); }}
              >
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300, color: legsElem.isFacade ? '#09aa89' : undefined }}>
                  swap_horiz
                </span>
              </OverlayBtn>
            </div>
          </Html>
        )}
      </group>
    );
  }

  // 7: Plokštė tarp šonų — VIENA plokštė VISAI FurnitureGroup (ne po vieną
  // kiekvienam kabinetui/sekcijai), analogiškai type 8 žemiau. Ši funkcija čia
  // gauna VISOS grupės safeTotalWidth = jau ATIMTĄ leftSideElementWidth/
  // rightSideElementWidth (žr. FurnitureGroup.jsx UnifiedBase kvietimą), t.y.
  // tiksliai TIKRŲ IŠORINIŲ šoninių sienelių (pirmo/paskutinio kabineto) išorinį
  // plotį — būtent tą patį plotį, kurį užpildo visų kabinetų karkasai sudėję
  // kartu (žr. FurnitureGroup.jsx komentarą prie UnifiedBase apie tikslų
  // pozicionavimą). Plokštė įgilinta safeWallThickness iš abiejų pusių (kad
  // sėdėtų TARP tikrų išorinių sienelių vidinių paviršių), plona (wallThickness
  // storio) ir pakelta virš grindų, su viršutiniu paviršiumi lygiai ties
  // safeHeight (=visualBaseHeight) — ten, kur SingleCabinet3D.jsx kiekvieno
  // kabineto vidinė lentynų struktūra atsiremia (bottomPanelThickness=0 šiam
  // tipui, žr. ten). Istorija: (1) pirmiausia piešta čia per VISĄ grupės plotį,
  // bet be šonų-siekia-grindis logikos — atrodė "kabanti"; (2) perkelta PER
  // KABINETĄ (SingleCabinet3D.jsx), su šonais siekiančiais grindų — bet tada
  // kiekviena sekcija gavo SAVO atskirą plokštę su tarpais ties vidinėmis
  // pertvaromis; (3) vartotojas paaiškino, kad realaus baldo bendra dugno
  // plokštė NESIDALINA — ji viena, per visą plotį, o vidinės sekcijų pertvaros
  // turi būti trumpesnės ir remtis į jos viršų (žr. ClickUp "2.0 Baldas.
  // Pagrindas. Plokštė (Tarp šonų). Padaryti dugno plokštę tarp šonų" —
  // "apatinė plokštė neturėtų dalintis ir turi eiti vienoje linijoje su
  // šoninėmis plokštėmis... dabar labiausiai trukdo skyriaus vidinės
  // pertvaros, tai jos tiesiog turėtų būti truputį trumpesnės"). Dabartinis
  // variantas: plokštė ČIA (grupės lygiu, viena), vidinės pertvaros sutrumpintos
  // SingleCabinet3D.jsx (žr. leftFloorReach/bottomOffsetFor ten).
  // (4) Y POZICIJA IR STORIS: du ankstesni bandymai (plona juosta prie viršaus;
  // paskui pilnas safeHeight aukščio blokas) abu buvo netikslūs. Vartotojas
  // GYVAI patikrino ir pasakė: "abiem atvejais [7 ir 8] jos yra pakilusios o
  // turėtų Y būti 0 ties baldo apačia, nes tai yra baldo pagrindas - grindys"
  // — apačia TIKRAI turi būti Y=0 (patvirtinta, nepakito). BET paskui, pamačius
  // pilno safeHeight aukščio bloką (~10cm, toks pat kaip visos bazės zonos
  // aukštis), vartotojas patikslino: "Plokštės storis turi būti toks pat kaip
  // ir visų kitų plokščių ~2cm" — t.y. PATI PLOKŠTĖ (kaip fizinis daiktas) yra
  // PLONA lenta, kaip ir kiekviena kita šio baldo konstrukcinė plokštė
  // (šoninės sienelės, dugno "Bottom wall" ir t.t., visos safeWallThickness
  // storio) — NE solidus blokas, užpildantis visą safeHeight zoną. Šios dvi
  // pastabos KARTU: plona (safeWallThickness aukščio Y ašimi, nes plokštė čia
  // GULI horizontaliai, kaip lentyna, o ne stovi vertikaliai kaip Padas/
  // Cokolis priekinė juosta), apačia lygiai ties Y=0 (tikros grindys). Virš
  // plokštės, iki safeHeight (kur prasideda vidinė lentynų struktūra / durys),
  // lieka ATVIRA/tuščia erdvė — tai atitinka referencinės ikonos (pagrindas/
  // 7.jpg) vaizdą: plona plokštė ant grindų, virš jos matoma tuščia tarpinė
  // erdvė iki baldo korpuso. safeWallThickness naudojamas TIESIOGIAI (ne
  // apytikslė "2cm" reikšmė), kad tiksliai sutaptų su visų kitų šio baldo
  // plokščių storiu (žr. SingleCabinet3D.jsx "Bottom wall"/šoninės sienelės —
  // tas pats wallThickness prop).
  if (type === 7) {
    const plateElem = elemProps('plate');
    const plateArgs = [Math.max(MIN_DIMENSION, safeTotalWidth - safeWallThickness * 2), safeWallThickness, safeDepth];
    const platePos = [0, safeWallThickness / 2, plateElem.isFacade ? 0.006 : 0];
    return (
      <group>
        {/* edgeOnly=false — plokštė IZOLIUOTA (vienintelis elementas šiame Pagrindo
            tipe, žr. SingleCabinet3D.jsx CabinetPanel 'top' identišką pataisymą).
            cabinetDepth={safeDepth} — be šito showHit (FrontTrimBox) likdavo FALSE,
            tad ši plokštė (plona, GULI horizontaliai, žr. komentarą aukščiau) neturėjo
            JOKIO ištempto nematomo hit-box'o — paspaudimas priklausydavo VIEN nuo
            tiesioginio spindulio pataikymo į ploną, status kameros kampu beveik
            briauną primenantį paviršių (ta pati problema, kuri jau buvo spręsta
            VISOMS kitoms šio tipo plokštėms, žr. CabinetPanel/TopBox hitDepthArg
            komentarus) — vartotojas: "uzvedu pelyte ant pagrindo plokstes briaunos,
            ji pakeicia spalva i zalia, paspaudziu ir nieko neivyksta" (hover pataiko,
            nes tai tęstinis, daug kartų kartojamas spindulio skaičiavimas, o vienkartinis
            click spindulys dažnai ne). */}
        <FrontTrimBox args={plateArgs} position={platePos} {...sideElemProps} {...plateElem} cabinetDepth={safeDepth} edgeOnly={false} />
      </group>
    );
  }

  // 8: Plokštė per visą plotį — tas pats storio principas kaip type 7 aukščiau
  // (žr. jo komentarą): plona, safeWallThickness aukščio Y ašimi, apačia ties
  // Y=0 (tikros grindys), skiriasi nuo type 7 TIK pločiu (čia safeTotalWidth,
  // ne įgilinta).
  if (type === 8) {
    const plateElem = elemProps('plate');
    const plateArgs = [safeTotalWidth, safeWallThickness, safeDepth];
    const platePos = [0, safeWallThickness / 2, plateElem.isFacade ? 0.006 : 0];
    return (
      <group>
        {/* edgeOnly=false + cabinetDepth — žr. type 7 identišką komentarą aukščiau. */}
        <FrontTrimBox args={plateArgs} position={platePos} {...sideElemProps} {...plateElem} cabinetDepth={safeDepth} edgeOnly={false} />
      </group>
    );
  }

  return null;
};

export default UnifiedBase;
